/**
 * TRON 注入探测：TIP-6963 + 按钱包路径精确匹配
 * 避免 TronLink / TokenPocket 等同页共存时误用对方 tronWeb。
 */

const TIP6963_ANNOUNCE = 'TIP6963:announceProvider'
const TIP6963_REQUEST = 'TIP6963:requestProvider'

/** @type {Map<string, { rdns: string, name: string, provider: object }>} */
const tip6963ByRdns = new Map()
/** @type {Set<(reason: string) => void>} */
const subscribers = new Set()
let inited = false

export const WALLET_TIP6963_RDNS = {
  tronLink: ['com.tronlink', 'org.tronlink', 'io.tronlink'],
  tokenPocket: ['pro.tokenpocket', 'com.tokenpocket'],
  bitget: ['com.bitget.web3', 'com.bitkeep.wallet'],
  okx: ['com.okex.wallet', 'com.okx.wallet'],
  binance: ['com.binance.wallet'],
  ave: ['ai.ave.wallet', 'com.ave.wallet'],
}

const RDNS_TO_WALLET_KEY = Object.fromEntries(
  Object.entries(WALLET_TIP6963_RDNS).flatMap(([key, rdnsList]) =>
    rdnsList.map((rdns) => [rdns, key]),
  ),
)

function sleepMs(ms) {
  return new Promise((r) => setTimeout(r, ms))
}

function getScope(scope) {
  if (scope) return scope
  if (typeof globalThis !== 'undefined') return globalThis
  return {}
}

function isTronWebLike(provider) {
  if (!provider || typeof provider !== 'object') return false
  if (typeof provider.contract === 'function') return true
  if (typeof provider.request === 'function') return true
  if (provider.trx && typeof provider.trx === 'object') return true
  return false
}

function onTipAnnounce(event) {
  const detail = event?.detail
  const info = detail?.info
  const provider = detail?.provider
  if (!info?.rdns || !provider || !isTronWebLike(provider)) return
  const rdns = String(info.rdns)
  const prev = tip6963ByRdns.get(rdns)
  if (prev?.provider === provider) return
  tip6963ByRdns.set(rdns, {
    rdns,
    name: String(info.name || ''),
    provider,
    walletKey: RDNS_TO_WALLET_KEY[rdns] || '',
  })
  notify('tip6963')
}

function notify(reason) {
  subscribers.forEach((fn) => {
    try {
      fn(reason)
    } catch (_e) {
      void _e
    }
  })
}

export function initTronInjectProbe() {
  if (inited || typeof window === 'undefined') return
  inited = true
  window.addEventListener(TIP6963_ANNOUNCE, onTipAnnounce)
  requestTronInjectProbeRefresh()
}

export function requestTronInjectProbeRefresh() {
  if (typeof window === 'undefined') return
  initTronInjectProbe()
  try {
    window.dispatchEvent(new Event(TIP6963_REQUEST))
  } catch (_e) {
    void _e
  }
}

export function subscribeTronInjectProbe(fn) {
  initTronInjectProbe()
  subscribers.add(fn)
  return () => subscribers.delete(fn)
}

export async function waitForTronInjectProbe({ timeoutMs = 900, intervalMs = 90 } = {}) {
  if (typeof window === 'undefined') return
  initTronInjectProbe()
  const deadline = Date.now() + timeoutMs
  let lastCount = tip6963ByRdns.size
  while (Date.now() < deadline) {
    requestTronInjectProbeRefresh()
    await sleepMs(intervalMs)
    if (tip6963ByRdns.size > lastCount) break
    lastCount = tip6963ByRdns.size
  }
}

function pickTip6963TronInject(walletKey) {
  initTronInjectProbe()
  const key = String(walletKey || '')
  for (const rdns of WALLET_TIP6963_RDNS[key] || []) {
    const entry = tip6963ByRdns.get(rdns)
    if (entry?.provider && isTronWebLike(entry.provider)) {
      return {
        tronWeb: entry.provider,
        tronLink: null,
        walletKey: key,
        source: 'tip6963',
      }
    }
  }
  return null
}

function pickTronLinkInject(g) {
  const link = g.tronLink
  if (link?.tronWeb && isTronWebLike(link.tronWeb)) {
    return { tronWeb: link.tronWeb, tronLink: link, walletKey: 'tronLink', source: 'tronLink' }
  }
  if (link && g.tronWeb && isTronWebLike(g.tronWeb)) {
    return { tronWeb: g.tronWeb, tronLink: link, walletKey: 'tronLink', source: 'tronLink' }
  }
  return null
}

function pickTokenPocketTronInject(g) {
  const candidates = [
    g.tokenpocket?.tronWeb,
    g.tokenpocket?.tron?.tronWeb,
    g.tp?.tronWeb,
    g.tp?.tron?.tronWeb,
  ]
  const tw = candidates.find(isTronWebLike)
  if (!tw) return null
  return {
    tronWeb: tw,
    tronLink: g.tokenpocket?.tronLink || g.tp?.tronLink || null,
    walletKey: 'tokenPocket',
    source: 'tokenPocket',
  }
}

function pickBitgetTronInject(g) {
  const candidates = [
    g.bitkeep?.tronWeb,
    g.bitkeep?.tron?.tronWeb,
    g.bitkeep?.tronLink?.tronWeb,
  ]
  const tw = candidates.find(isTronWebLike)
  if (!tw) return null
  return {
    tronWeb: tw,
    tronLink: g.bitkeep?.tronLink || null,
    walletKey: 'bitget',
    source: 'bitget',
  }
}

function pickOkxTronInject(g) {
  const candidates = [g.okxwallet?.tronWeb, g.okxwallet?.tron?.tronWeb, g.okxwallet?.tronLink?.tronWeb]
  const tw = candidates.find(isTronWebLike)
  if (!tw) return null
  return {
    tronWeb: tw,
    tronLink: g.okxwallet?.tronLink || null,
    walletKey: 'okx',
    source: 'okx',
  }
}

function pickBinanceTronInject(g) {
  const candidates = [g.BinanceChain?.tronWeb, g.binancew3w?.tronWeb, g.binancew3w?.tron?.tronWeb]
  const tw = candidates.find(isTronWebLike)
  if (!tw) return null
  return {
    tronWeb: tw,
    tronLink: null,
    walletKey: 'binance',
    source: 'binance',
  }
}

function pickAveTronInject(g) {
  const candidates = [
    g.avewallet?.tronWeb,
    g.aveWallet?.tronWeb,
    g.ave?.tronWeb,
  ]
  const tw = candidates.find(isTronWebLike)
  if (!tw) return null
  return {
    tronWeb: tw,
    tronLink: null,
    walletKey: 'ave',
    source: 'ave',
  }
}

const LEGACY_PICKERS = {
  tronLink: pickTronLinkInject,
  tokenPocket: pickTokenPocketTronInject,
  bitget: pickBitgetTronInject,
  okx: pickOkxTronInject,
  binance: pickBinanceTronInject,
  ave: pickAveTronInject,
}

/** 按钱包 key 解析专属 TRON 注入（不回落其它钱包的 tronWeb） */
export function resolveTronInjectForWallet(walletKey, scope) {
  const key = String(walletKey || '').trim()
  if (!key) return null
  const fromTip = pickTip6963TronInject(key)
  if (fromTip) return fromTip
  const g = getScope(scope)
  const pick = LEGACY_PICKERS[key]
  return pick ? pick(g) : null
}

export function providerMatchesTronWalletKey(provider, walletKey) {
  const key = String(walletKey || '').trim()
  if (!provider || !key) return false
  const inject = resolveTronInjectForWallet(key)
  if (!inject?.tronWeb) return false
  return inject.tronWeb === provider
}

export function isTronWalletKeyInjectAvailable(walletKey, scope) {
  return !!resolveTronInjectForWallet(walletKey, scope)?.tronWeb
}

/** 从 TIP6963 注册表精确匹配 provider → walletKey */
export function inferTronWalletKeyFromTip6963Provider(provider) {
  if (!provider) return ''
  initTronInjectProbe()
  for (const entry of tip6963ByRdns.values()) {
    if (entry.provider === provider && entry.walletKey) {
      return entry.walletKey
    }
  }
  return ''
}
